import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import integrationsService from "../../../services/web/integrations-service.js";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import Button from "../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../utils/aggrid-utils.js";
import integrationCellRenderer from "./components/integration-cell-renderer.jsx";
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";
import projectUtils from "../../../utils/project-utils.js";
import {toast} from "react-toastify";
import SelectComponent from "../../../components/select/select-component.jsx";
import IntegrationSharedImage from "./components/integration-shared-image.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import {OdsDocument} from "odf-kit";

const initialFilters = {
    status: {value: "all", label: "Todos"},
    laneCode: "",
    capturePlace: "",
    integrations: []
}

const formatIntegrationStatus = (value) => {
    const status = value?.status;

    if (status === "ON") return "Ligada";
    if (status === "OFF") return "Desligada";

    return "Não integrada";
};

const Integrations = () => {
    const [data, setData] = useState([])
    const [filters, setFilters] = useState(initialFilters);
    const [integrationSelected, setIntegrationSelected] = useState([]);

    const [open, setOpen] = useState(false);

    const [columnDefs,] = useState([
        {field: "code", headerName: "ID", width: 100},
        {field: "capturePlace", headerName: "Ponto de coleta", sort: "asc", flex: 2},
        {field: "route", headerName: "Sentido", flex: 1},
    ]);

    const gridRef = useRef(undefined);

    const {data: dataFetch, isLoading, isSuccess, isRefetching, refetch} = useQuery({
        queryFn: () => integrationsService.list(),
        queryKey: ["integrations"]
    });

    const {data: imageRequest, refetch: refetchImage} = useQuery({
        queryFn: () => integrationsService.imageRequest(),
        queryKey: ["imageRequest"]
    });

    const {refetch: refetchPrfIntegrations, isFetching: isExportingPrf} = useQuery({
        queryFn: () => integrationsService.getIntegrations(),
        queryKey: ["prfIntegrations"],
        enabled: false
    });

    const integrations = dataFetch?.integrations ?? [];
    const hasPrfIntegration = integrations.some(integration => integration.toLowerCase() === "prf");

    const verifyIfAllOptionsIsOn = (api, integration) => {
        let hasOn = false;
        let hasOff = false;

        api.forEachNode(node => {
            const status = node.data[integration]?.status;

            if (status === 'ON') hasOn = true;
            if (status === 'OFF') hasOff = true;
        });

        const shouldSelect = hasOn && !hasOff;

        setIntegrationSelected(prev => shouldSelect ? prev.includes(integration) ? prev : [...prev, integration] : prev.filter(p => p !== integration));
    };

    useEffect(() => {
        if (!isSuccess || !gridRef.current) return

        if (integrations.length) {
            const colDefs = [...columnDefs];
            integrations?.forEach(integration => {
                colDefs.push({
                    field: integration,
                    headerName: integration,
                    sortable: false,
                    width: 125,
                    cellRenderer: integrationCellRenderer,
                    valueFormatter: ({value}) => formatIntegrationStatus(value),
                    useValueFormatterForExport: true,
                    cellRendererParams: {integration, verifyIfAllOptionsIsOn}
                });
            });

            gridRef.current.api.setGridOption("columnDefs", colDefs);
        }

        setData(dataFetch?.lanes ?? []);
    }, [dataFetch, isSuccess, gridRef, isRefetching]);

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const handleSelectAll = (includes, integration) => {
        const status = !includes ? "ON" : "OFF";

        gridRef.current?.api.forEachNode(node => {
            const data = node.data;
            if (!data[integration].status || data[integration].status === "ND") return;

            node.setData({...node.data, [integration] : {status}});
        });

        if (includes) {
            setIntegrationSelected(prev => prev.filter(i => i !== integration))
        } else {
            setIntegrationSelected(prev => [...prev, integration])
        }
    }

    const onFirstDataRendered = ({api}) => {
        integrations?.map(i => verifyIfAllOptionsIsOn(api, i))
    }

    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));

    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));

    const search = useCallback(() => {
        const lanes = dataFetch?.lanes || [];

        const dataFiltered = lanes.filter(item =>
            Object.entries(filters).every(([key, filter]) => {
                if (!filter) return true;

                if (key === 'status') {
                    if (filter.value === 'all') return true;
                    return item.status === filter.value;
                }

                if (key === 'laneCode') {
                    const lanesArrRange = filter.split('-');
                    const lanesArr = filter.split(',');

                    let laneCodes = lanesArr.map(e => parseInt(e));

                    if (lanesArrRange.length === 2) {
                        let codeInit = parseInt(lanesArrRange[0]);
                        const codeEnd = parseInt(lanesArrRange[1]);

                        if (codeInit && codeEnd) {
                            laneCodes = [];
                            do {
                                laneCodes.push(codeInit);
                                codeInit++;
                            }
                            while (codeInit <= codeEnd);
                        }
                    }

                    return laneCodes.includes(item.code);
                }

                if (key === 'integrations') {
                    if (!filter.length) return true;
                    return filter.some(i => item[i.value]?.status && item[i.value].status !== "ND");
                }

                if (item[key] === undefined || item[key] === null) return false;

                return projectUtils.removeAccents(item[key].toString()).toLowerCase()
                    .includes(projectUtils.removeAccents(filter).toLowerCase());
            })
        );

        setData(dataFiltered);
    }, [dataFetch, filters]);

    const clear = useCallback(() => {
        setFilters(initialFilters);
        setData(dataFetch?.lanes)
    }, [dataFetch]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                search();
            }
            if (event.key === "Escape") {
                clear();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [search, clear]);

    const save = () => {
        const gridDataByCode = new Map();

        gridRef.current.api.forEachNode(node => gridDataByCode.set(node.data.code, node.data));

        const updatedData = dataFetch.lanes.flatMap(lane => {
            const gridLane = gridDataByCode.get(lane.code);
            if (!gridLane) return [];

            return integrations
                .filter(type => lane[type]?.status !== gridLane[type]?.status && gridLane[type]?.status !== undefined)
                .map(type => ({laneCode: lane.code, type, status: { status: gridLane[type].status }}));
        });

        if (!updatedData.length) {
            projectUtils.swalErrorMessage("", "Nenhuma integração alterada", "Ok");
            return;
        }

        integrationsService.updateStatus(updatedData)
            .then(() => {
                toast.success("Salvo com sucesso");
            })
            .catch(error => {
                console.error(error);
                let message = "Não foi possível salvar.";
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            });
    }

    const exportData = () => {
        gridRef.current?.api.exportDataAsCsv({
            fileName: "integracoes.csv",
            columnSeparator: ";"
        });
    };

    const exportPrfData = async () => {
        try {
            const {data} = await refetchPrfIntegrations({throwOnError: true});
            const headers = data.headers.map(header => header === "SENTIDO1" || header === "SENTIDO2" ? "SENTIDO" : header);
            const rows = data.rows.map(row => [...row]);
            const newColumnIndex = headers.indexOf("NOVO");
            const odsDocument = new OdsDocument();
            const sheet = odsDocument.addSheet("Integrações PRF");

            if (newColumnIndex !== -1) {
                headers.splice(newColumnIndex, 1);
            }

            const toStringCell = (value) => ({
                value: String(value ?? "").toUpperCase(),
                type: "string"
            });
            const columnWidths = headers.map(header => toStringCell(header).value.length);
            let rowIndex = 0;

            sheet.addRow(headers.map(toStringCell), {
                bold: true,
                textAlign: "center",
                verticalAlign: "middle",
                border: "thin solid #000000"
            });
            sheet.setRowHeight(rowIndex++, "60pt");

            rows.forEach(row => {
                if (newColumnIndex !== -1) {
                    row.splice(newColumnIndex, 1);
                }

                const values = [
                    ...row.map(toStringCell),
                    ...Array(Math.max(headers.length - row.length, 0)).fill(null).map(toStringCell)
                ];

                values.forEach((cell, index) => {
                    columnWidths[index] = Math.max(columnWidths[index], cell.value.length);
                });

                sheet.addRow(values, {
                    verticalAlign: "middle",
                    border: "thin solid #000000"
                });
                sheet.setRowHeight(rowIndex++, "14.25pt");
            });

            columnWidths.forEach((width, index) => {
                sheet.setColumnWidth(index, `${Math.min(Math.max(width * 0.22, 2.5), 15)}cm`);
            });

            sheet.addRow([]);
            sheet.setRowHeight(rowIndex++, "14.25pt");

            const bytes = await odsDocument.save();
            const blob = new Blob([bytes], {
                type: "application/vnd.oasis.opendocument.spreadsheet"
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = "integracoes-prf.ods";
            link.click();

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            toast.error("Não foi possível exportar as integrações da PRF.");
        }
    };

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>
                        Integrações
                    </Template.Title>
                    <div className="flex gap-2">
                        {
                            imageRequest?.length > 0 &&
                            <Button onClick={() => setOpen(true)}>
                                Compartilhamento de Imagem
                            </Button>
                        }
                        {hasPrfIntegration ? (
                            <Menu as="div" className="flex items-center text-white h-[32px]">
                            <MenuButton
                                type="button"
                                className="flex justify-center cursor-pointer h-[32px] items-center gap-1 focus:ring-2 focus:outline-none font-medium rounded shadow text-[13px] px-4 py-2 text-center text-white bg-green-600 hover:bg-green-700 focus:ring-green-300"
                            >
                                <FaFileExcel/>Exportar
                            </MenuButton>
                            <Transition
                              enter="transition ease-out duration-75"
                              enterFrom="opacity-0 scale-95"
                              enterTo="opacity-100 scale-100"
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100 scale-100"
                              leaveTo="opacity-0 scale-95"
                            >
                                <MenuItems
                                  modal={false}
                                  className="absolute top-[42px] right-2 mt-1 overflow-hidden w-36 origin-top-right rounded-md shadow border text-[13px] bg-white focus:outline-none z-[9999] text-gray-700 flex flex-col border-[#bdbdbd]"
                                >
                                    <MenuItem>
                                        <button
                                            type="button"
                                            onClick={exportData}
                                            disabled={!data.length}
                                            className="w-full text-[13px] py-2 px-4 cursor-pointer text-left text-gray-700 block text-sm hover:bg-green-600 hover:text-white"
                                        >
                                            Exportar
                                        </button>
                                    </MenuItem>
                                        <MenuItem>
                                            <button
                                                type="button"
                                                onClick={exportPrfData}
                                                disabled={isExportingPrf}
                                                className="w-full text-[13px] py-2 px-4 cursor-pointer text-left text-gray-700 block text-sm hover:bg-green-600 hover:text-white"
                                            >
                                                Exportar - PRF
                                            </button>
                                        </MenuItem>
                                </MenuItems>
                            </Transition>
                            </Menu>
                        ) : (
                            <Button target="success" onClick={exportData} disabled={!data.length}>
                                <FaFileExcel/>Exportar
                            </Button>
                        )}
                    </div>
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Body>
                            <SentryRow>
                                <div className={"col-span-2"}>
                                    <SelectComponent
                                        value={filters.status}
                                        options={[{value: "all", label: "Todos"}, {value: true, label: "Ativa"}, {value: false, label: "Inativa"}]}
                                        label={"Status"}
                                        onChange={(value) => handleSelect("status", value)}
                                        isClearable={false}
                                    />
                                </div>

                                <div className={"col-span-2"}>
                                    <InputForm
                                        id={"laneCode"}
                                        label={"Id da câmera"}
                                        name={"laneCode"}
                                        value={filters.laneCode}
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className={"col-span-5"}>
                                    <InputForm
                                        id={"capturePlace"}
                                        label={"Ponto de coleta"}
                                        name={"capturePlace"}
                                        value={filters.capturePlace}
                                        onChange={handleInput}
                                    />
                                </div>

                                <div className="flex justify-between col-span-3 gap-4">
                                    <SelectComponent
                                        classNameWrapper={"w-[calc(100%-105px)]"}
                                        value={filters.integrations}
                                        options={integrations?.map(i => ({value: i, label: i}))}
                                        label={"Integrações"}
                                        onChange={(value) => handleSelect("integrations", value)}
                                        isMulti
                                    />

                                    <div className="flex gap-2">
                                        <Button target={"default"} onClick={clear}>
                                            <FaEraser/>
                                        </Button>

                                        <Button onClick={search}>
                                            <FaSearch/>
                                        </Button>
                                    </div>
                                </div>
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <div style={{height: "calc(100vh - 255px)"}}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data}
                                    columnDefs={columnDefs}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    getRowId={({ data }) => data.code}
                                    theme={theme}
                                    onFirstDataRendered={onFirstDataRendered}
                                />
                            </div>

                            <Menu as="div" className="flex items-center text-white mt-2 justify-end">
                                <MenuButton type="button" className={"font-bold flex justify-center cursor-pointer h-[32px] items-center gap-1 focus:ring-1 focus:outline-none rounded-md shadow text-[13px] px-4 py-2 text-center text-white bg-[#063A6C] hover:bg-blue-800 focus:ring-blue-300 disabled:hover:bg-blue-700 disabled:bg-[#9B9B9B] disabled:pointer-events-none disabled:opacity-70"} disabled={!integrations?.length}>
                                    Configurar integrações
                                </MenuButton>
                                <Transition
                                    enter="transition ease-out duration-75"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <MenuItems
                                        anchor="top end"
                                        static={true}
                                        className="w-auto origin-top-right rounded-md shadow border text-[13px] bg-white [--anchor-gap:8px] focus:outline-none z-[100] text-gray-700 flex flex-col border-[#bdbdbd] p-1 items-start space-y-1"
                                    >
                                        {
                                            integrations?.map(i => (
                                                <MenuItem key={i}>
                                                    <span
                                                        className={"cursor-pointer w-full py-[6px] px-[5px] rounded-sm hover:bg-[#d3d3d3]" + (integrationSelected.includes(i) ? " bg-[#d3d3d3]" : "")}
                                                        onClick={() => handleSelectAll(integrationSelected.includes(i), i)}
                                                    >
                                                         {i} - {integrationSelected.includes(i) ? "Desativar" : "Ativar"} todos
                                                    </span>
                                                </MenuItem>
                                            ))
                                        }
                                    </MenuItems>
                                </Transition>
                            </Menu>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Body>
                            <Button className={"justify-self-end"} onClick={save} disabled={!integrations?.length}>
                                Aplicar
                            </Button>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <IntegrationSharedImage
                open={open}
                setOpen={setOpen}
                data={imageRequest}
                refetchImage={refetchImage}
                refetch={refetch}
                dataFetch={dataFetch}
            />
        </>
    );
};

export default Integrations;
